import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Role } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// #274: Rollen-Verwaltung. `kind` ist bewusst KEIN Eingabefeld — System-Rollen sind
// kuratiert und entstehen nur über die Migration.
export interface CreateRoleInput {
  name: string;
  description?: string;
}

export function useCreateRole(): UseMutationResult<Role, Error, CreateRoleInput> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (input) => getClient().post<Role>("/roles", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("roles") }),
  });
}

export function useUpdateRole(): UseMutationResult<Role, Error, { id: string; description: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, description }) => getClient().patch<Role>(`/roles/${id}`, { description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("roles") }),
  });
}

export function useDeleteRole(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("roles") }),
  });
}

// Mitgliedschaft — invalidiert BEIDE Seiten (Rollen-Detail + die Rollen-Karte des Users).
//
// ⚠ #331: eine Rolle ist eine GLOBALE Menge von Personen, sonst nichts. Der frühere
// Geltungsbereich (`scope = process-instance:<uuid>`, #302 UC-4) ist ersatzlos gestrichen —
// der Master lehnt das Feld jetzt mit 400 ab (strict). „Vertretung für einen Vorgang" wird
// im Setup der Einrichtung gewählt, nicht mehr an der Rolle.
export function useAssignRole(): UseMutationResult<void, Error, { roleId: string; userId: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ roleId, userId }) => getClient().put<void>(`/roles/${roleId}/members/${userId}`, {}),
    onSuccess: (_d, { userId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.resource("roles") });
      void qc.invalidateQueries({ queryKey: queryKeys.subResource("users", userId, "roles") });
    },
  });
}

export function useUnassignRole(): UseMutationResult<void, Error, { roleId: string; userId: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ roleId, userId }) => getClient().delete<void>(`/roles/${roleId}/members/${userId}`),
    onSuccess: (_d, { userId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.resource("roles") });
      void qc.invalidateQueries({ queryKey: queryKeys.subResource("users", userId, "roles") });
    },
  });
}
